/**
 * Sound effects of the player (AudioPlayingController.cs):
 *  ok              → collect_item.wav   (each correct item; muteSoundAtCorrectAnswer)
 *  wrong           → wrong.wav          (each error; muteSoundAtWrongAnswer)
 *  take            → Tic.mp3            (picking up a draggable; muteFx)
 *  congratulations → bien.mp3           (end of slide / project; muteSoundCongratulations)
 *
 * Files are staged under /libraries/fx by tools/src/stage-libraries.mjs.
 */
import type { ProjectSettings } from "@makeit/schema";
import { libraryUrl } from "../data/media";

export type Fx = "ok" | "wrong" | "take" | "congratulations";

const FILES: Record<Fx, string> = {
  ok: "fx/ok.wav",
  wrong: "fx/wrong.wav",
  take: "fx/take.mp3",
  congratulations: "fx/congratulations.mp3",
};

const cache = new Map<Fx, HTMLAudioElement>();

function isMuted(fx: Fx, s: ProjectSettings): boolean {
  switch (fx) {
    case "ok":
      return s.muteSoundAtCorrectAnswer;
    case "wrong":
      return s.muteSoundAtWrongAnswer;
    case "congratulations":
      return s.muteSoundCongratulations;
    case "take":
      return s.muteFx;
  }
}

/** Plays an effect unless the project mutes it. Safe to call before any user gesture (errors are swallowed). */
export function playFx(fx: Fx, settings: ProjectSettings, delayMs = 0): void {
  if (isMuted(fx, settings)) return;
  let audio = cache.get(fx);
  if (!audio) {
    audio = new Audio(libraryUrl(FILES[fx]));
    audio.preload = "auto";
    cache.set(fx, audio);
  }
  const a = audio;
  const go = () => {
    try {
      a.currentTime = 0;
      void a.play().catch(() => undefined);
    } catch {
      /* ignore */
    }
  };
  if (delayMs > 0) window.setTimeout(go, delayMs);
  else go();
}

/** Warm the cache so the first effect is not delayed by a network round trip. */
export function preloadFx(): void {
  (Object.keys(FILES) as Fx[]).forEach((fx) => {
    if (!cache.has(fx)) {
      const a = new Audio(libraryUrl(FILES[fx]));
      a.preload = "auto";
      cache.set(fx, a);
    }
  });
}
